import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { portalMe } from '../services/api';
import { Buildings, House, FileText, ChatCircleDots, User, CalendarBlank, CurrencyEur } from '@phosphor-icons/react';

export default function TenantDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalMe()
      .then(res => setProfile(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-dashboard">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          Willkommen, {profile?.name?.split(' ')[0]}
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">
          Ihre persönliche Übersicht im Mieter-Portal
        </p>
      </div>

      {/* Property & Unit Card */}
      {profile?.unit && profile?.property ? (
        <div className="bg-white border border-gray-200 p-6 sm:p-8" data-testid="tenant-unit-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Buildings weight="bold" className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Ihre Wohnung</p>
              <h2 className="font-heading text-2xl font-black text-slate-900 mt-1">
                {profile.unit.name}
              </h2>
              <p className="text-slate-600">{profile.property.name}</p>
              <p className="text-sm text-slate-500">{profile.property.address}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-gray-200">
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Fläche</p>
              <p className="font-mono font-bold text-slate-900 mt-1">{profile.unit.size_sqm || '—'} m²</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Etage</p>
              <p className="font-mono font-bold text-slate-900 mt-1">{profile.unit.floor ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Miete</p>
              <p className="font-mono font-bold text-slate-900 mt-1">{formatCurrency(profile.unit.rent_amount)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Personen</p>
              <p className="font-mono font-bold text-slate-900 mt-1">{profile.personen_anzahl || 1}</p>
            </div>
          </div>

          {(profile.lease_start || profile.lease_end) && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex items-center gap-2 text-sm text-slate-500">
              <CalendarBlank weight="bold" className="w-4 h-4" />
              <span>Mietzeit: {profile.lease_start || '—'} bis {profile.lease_end || 'unbefristet'}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 p-6">
          <p className="font-medium text-amber-800">Ihrem Konto ist noch keine Wohneinheit zugewiesen.</p>
          <p className="text-sm text-amber-700 mt-1">Bitte kontaktieren Sie Ihren Vermieter.</p>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Schnellzugriff</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/portal/dokumente" className="bg-white border border-gray-200 p-6 hover:border-emerald-500 hover:shadow-md transition-all group" data-testid="quick-action-dokumente">
            <FileText weight="bold" className="w-8 h-8 text-emerald-700 mb-3" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Meine Dokumente</h3>
            <p className="text-sm text-slate-500 mt-1">Abrechnungen & Unterlagen</p>
          </Link>
          <Link to="/portal/abrechnung" className="bg-white border border-gray-200 p-6 hover:border-emerald-500 hover:shadow-md transition-all group" data-testid="quick-action-abrechnung">
            <FileText weight="bold" className="w-8 h-8 text-emerald-700 mb-3" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Nebenkosten</h3>
            <p className="text-sm text-slate-500 mt-1">Jahresabrechnung einsehen</p>
          </Link>
          <Link to="/portal/messages" className="bg-white border border-gray-200 p-6 hover:border-emerald-500 hover:shadow-md transition-all group" data-testid="quick-action-messages">
            <ChatCircleDots weight="bold" className="w-8 h-8 text-emerald-700 mb-3" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Nachrichten</h3>
            <p className="text-sm text-slate-500 mt-1">Mit Vermieter kommunizieren</p>
          </Link>
          <Link to="/portal/profile" className="bg-white border border-gray-200 p-6 hover:border-emerald-500 hover:shadow-md transition-all group" data-testid="quick-action-profile">
            <User weight="bold" className="w-8 h-8 text-emerald-700 mb-3" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Mein Profil</h3>
            <p className="text-sm text-slate-500 mt-1">Persönliche Daten verwalten</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
